import React, { useState, useRef } from 'react';
import { Play } from 'lucide-react';

function Carousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef(null);

  const slides = [
    {
      title: "THE MASTER'S STUDIOS - BANARASI MRIDANGA MASTERCLASS",
      description: 'Learn authentic rhythms and explore premium handmade instruments.',
      image: 'https://img.youtube.com/vi/GPgGYcsMi8U/maxresdefault.jpg',
      link: 'https://www.youtube.com/watch?v=GPgGYcsMi8U'
    },
    {
      title: 'SPECIAL NATAI & BALRAM MRIDANGA! 🎵🥁',
      description: 'Experience the perfect blend of traditional and modern rhythms.',
      image: 'https://img.youtube.com/vi/p3HveGcLn80/maxresdefault.jpg',
      link: 'https://www.youtube.com/watch?v=p3HveGcLn80'
    },
    {
      title: 'PREMIUM SELECTION & EXCLUSIVE OFFERS',
      description: 'Find your perfect rhythm with our latest studio instrument lineup.',
      image: 'https://img.youtube.com/vi/HXaY-ihC57Q/maxresdefault.jpg',
      link: 'https://www.youtube.com/watch?v=HXaY-ihC57Q'
    }
  ];

  // 1. Synchronize state when the user naturally swipes or scrolls manually
  const handleScroll = () => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    // Calculate current slide index based on the scrolled horizontal offset
    const scrollPosition = container.scrollLeft;
    const slideWidth = container.clientWidth;

    if (slideWidth > 0) {
      const activeIndex = Math.round(scrollPosition / slideWidth);
      if (activeIndex !== currentSlide) {
        setCurrentSlide(activeIndex);
      }
    }
  };

  // 2. Programmatically scroll to the clicked element when clicking an indicator dot
  const scrollToSlide = (idx) => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const slideWidth = container.clientWidth;

    container.scrollTo({
      left: idx * slideWidth,
      behavior: 'smooth'
    });
    setCurrentSlide(idx);
  };

  return (
    <section className="overflow-hidden rounded-md bg-[#F2F1EC] p-4">
      {/* Scroll Track Wrapper */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto scroll-smooth snap-x snap-mandatory [-ms-scrollbar-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden rounded-md border border-[#E1DFD7] shadow-xs"
      >
        {slides.map((slide, idx) => (
          <div key={idx} className="flex items-stretch snap-start w-full flex-shrink-0">
            <div className="flex flex-1 flex-col rounded-md w-full relative overflow-hidden aspect-video max-h-[460px]">

              <a
                href={slide.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full h-full cursor-pointer transition-opacity duration-300 hover:opacity-95 bg-[#1E1D1A] group"
              >
                <div
                  className="w-full h-full bg-center bg-no-repeat bg-contain flex flex-col justify-end p-6 sm:p-10 relative"
                  style={{
                    backgroundImage: `linear-gradient(to top, rgba(30,29,26,0.9) 0%, rgba(30,29,26,0.3) 50%, transparent 100%), url(${slide.image})`
                  }}
                >
                  {/* Minimalist Centered Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center rounded-full bg-[#FDFDFB]/90 text-[#9A7B56] shadow-md transition-transform duration-300 group-hover:scale-110 group-hover:bg-[#9A7B56] group-hover:text-white">
                      <Play className="w-6 h-6 sm:w-7 sm:h-7 fill-current translate-x-0.5" />
                    </div>
                  </div>

                  <div className="max-w-xl text-left z-10">
                    <span className="text-[10px] text-[#9A7B56] uppercase tracking-widest font-bold bg-[#FDFDFB]/90 px-2 py-0.5 rounded-xs mb-2 inline-block">
                      Featured Session
                    </span>
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-light text-[#FDFDFB] uppercase tracking-wide leading-snug drop-shadow-xs">
                      {slide.title}
                    </h2>
                    <p className="text-xs sm:text-sm font-light mt-1.5 text-[#D2D0C5] tracking-wide drop-shadow-xs">
                      {slide.description}
                    </p>
                  </div>
                </div>
              </a>

            </div>
          </div>
        ))}
      </div>

      {/* Slide Indicators */}
      <div className="flex justify-center gap-2 mt-4" role="tablist" aria-label="Slideshow navigation">
        {slides.map((_, idx) => (
          <button
            key={idx}
            type="button"
            role="tab"
            aria-selected={idx === currentSlide}
            aria-label={`Go to slide ${idx + 1}`}
            onClick={() => scrollToSlide(idx)}
            className={`transition-all duration-300 rounded-full h-1.5 focus:outline-none ${idx === currentSlide
              ? 'w-5 bg-[#9A7B56]'
              : 'w-1.5 bg-[#E1DFD7] hover:bg-[#A6A29A]'
              }`}
          />
        ))}
      </div>
    </section>
  );
}

export default Carousel;